import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiPlus, FiEdit2, FiTrash2, FiPlay, FiDownload, FiEye } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import reportsService from '../../services/reportsService';
import CommonModal from '../../services/CommonModal';

const Reports = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  // Fetch reports
  const { data: reportsData, isLoading } = useQuery({
    queryKey: ['reports', filters],
    queryFn: () => reportsService.getAllReports(filters),
  });


  const confirmDelete = () => {
    console.log("uuuu")
    handleDelete(selectedId);
    setShowDeleteModal(false);
  };

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: reportsService.deleteReport,
    onSuccess: () => {
      queryClient.invalidateQueries(['reports']);
      toast.success('Report deleted successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to delete report');
    },
  });

  const handleDelete = async (id, name) => {
    await deleteMutation.mutateAsync(id);
  };

  const handleGenerate = (id) => {
    navigate(`/app/reports/${id}/view`);
  };

  const getReportTypeLabel = (type) => {
    const types = reportsService.getReportTypes();
    return types.find(t => t.value === type)?.label || type;
  };

  const getReportTypeBadge = (type) => {
    const colors = {
      project_status: 'bg-blue-100 text-blue-800',
      resource_utilization: 'bg-green-100 text-green-800',
      budget_analysis: 'bg-yellow-100 text-yellow-800',
      time_tracking: 'bg-purple-100 text-purple-800',
      task_progress: 'bg-pink-100 text-pink-800',
      custom: 'bg-gray-100 text-gray-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[type] || colors.custom}`}>
        {getReportTypeLabel(type)}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Reports</h1>
          <p className="mt-1 text-sm text-gray-600">
            Create, manage, and generate reports for your projects
          </p>
        </div>
        <Link
          to="/app/reports/new"
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
        >
          <FiPlus className="mr-2" />
          Create Report
        </Link>
      </div>

      {/* Reports List */}
      {isLoading ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading reports...</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {reportsData?.data && reportsData.data.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Report Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created By
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Generated
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Visibility
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reportsData.data.map((report) => (
                    <tr key={report.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-medium text-gray-900">{report.name}</div>
                          {report.description && (
                            <div className="text-sm text-gray-500">{report.description}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getReportTypeBadge(report.report_type)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {report.creator?.first_name} {report.creator?.last_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 ">
                        {report.last_generated_at
                          ? new Date(report.last_generated_at).toLocaleDateString()
                          : 'Never'
                        }
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium custom-bg-green ${report.is_public
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                          }`}>
                          {report.is_public ? 'Public' : 'Private'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => handleGenerate(report.id)}
                            className="text-green-600 hover:text-green-900"
                            title="Generate Report"
                          >
                            <FiPlay />
                          </button>
                          <button
                            onClick={() => navigate(`/app/reports/${report.id}`)}
                            className="text-blue-600 hover:text-blue-900"
                            title="View Details"
                          >
                            <FiEye />
                          </button>
                          <Link
                            to={`/app/reports/${report.id}/edit`}
                            className="text-blue-600 hover:text-blue-900"
                            title="Edit"
                          >
                            <FiEdit2 />
                          </Link>
                          <button
                            onClick={() => {
                              console.log("kkk")
                              setSelectedId(report.id, report.name);
                              setShowDeleteModal(true);
                            }}

                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                          >

                            <FiTrash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500 mb-4">No reports found. Create your first report!</p>
              <Link
                to="/app/reports/new"
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                <FiPlus className="mr-2" />
                Create Report
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Quick Report Templates */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">Quick Report Templates</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {reportsService.getReportTypes().map((type) => (
            <Link
              key={type.value}
              to={`/app/reports/new?type=${type.value}`}
              className="p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:shadow-md transition"
            >
              <h3 className="font-medium text-gray-900 mb-2">{type.label}</h3>
              <p className="text-sm text-gray-600">
                Create a new {type.label.toLowerCase()}
              </p>
            </Link>
          ))}
        </div>
      </div>
      <CommonModal
        isOpen={showDeleteModal}
        title="Delete Vendor"
        message="Are you sure you want to delete this Report?"
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedId(null);
        }}
        onConfirm={confirmDelete}
        type="error"
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>



  );
};

export default Reports;


